import React from "react";
import { plainText } from "../../atoms/text/text";
import type { Membership } from "./channel-lineup.types";
import {
  type IndicatorStateLabels,
  MEMBERSHIPS,
  TOOLTIP_ATTRIBUTE,
  indicatorAccessibleName
} from "./indicator.shared";
import styles from "./indicator.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** Not a client boundary. */

export interface TooltipTriggerProps {
  /** The tooltip's wording. Empty or blank renders a plain button, no tooltip. */
  content: string;
  /** The trigger's accessible name. Required — what it draws carries none. */
  label: string;
  /** What the trigger draws: a badge, a logo tile. */
  children?: React.ReactNode;
  /** Layout only. */
  className?: string;
}

/**
 * A focusable trigger for a short panel — ARIA APG "Tooltip": a real `<button>`, described by a
 * `role="tooltip"` panel that `TooltipScope` renders. Works only inside a `TooltipScope`.
 */
function TooltipTriggerImpl({ content, label, children, className }: TooltipTriggerProps) {
  const repeatsName = content.trim().toLowerCase() === label.trim().toLowerCase();
  const tip = content.trim() === "" ? undefined : repeatsName ? "" : content;

  return (
    <span
      className={[styles.triggerRoot, className].filter(Boolean).join(" ")}
      {...{ [TOOLTIP_ATTRIBUTE]: tip }}
    >
      <button aria-label={label} className={styles.trigger} type="button">
        {children}
      </button>
    </span>
  );
}
export const TooltipTrigger = withSafeRender(TooltipTriggerImpl, "TooltipTrigger");

export interface IndicatorProps {
  /** Which badge to draw: ✓ included, $ add-on, ★ promo (GR2). */
  membership: Membership;
  /** The row's channel. Markup in it is stripped before it is announced (GR9). */
  channelName: string;
  /** The column this cell sits under — the package at the top of it (GR2). */
  packageLabel: string;
  /** GR3 — the tooltip's prefix. Absent, the tooltip is the package name alone. */
  tooltipPrefix?: string;
  /** AX3 — the cell states' screen-reader wording. */
  stateLabels?: IndicatorStateLabels;
  /** Layout only. */
  className?: string;
}

/**
 * One package cell's indicator: a filled round badge that names its channel, its package and its
 * state to a screen reader (AX3) and shows GR3's tooltip on hover, tap and focus.
 */
function IndicatorImpl({
  membership,
  channelName,
  packageLabel,
  tooltipPrefix,
  stateLabels,
  className
}: IndicatorProps) {
  const known = MEMBERSHIPS.includes(membership);
  const prefix = plainText((tooltipPrefix ?? "").trim());
  const packageName = plainText(packageLabel);

  return (
    <TooltipTrigger
      className={[styles.indicator, known ? styles[membership] : undefined, className].filter(Boolean).join(" ")}
      /* GR9 — the visible tooltip gets the same markup reduction as the name. */
      content={prefix === "" ? packageName : `${prefix} ${packageName}`}
      label={indicatorAccessibleName(channelName, packageLabel, membership, stateLabels)}
    />
  );
}
export const Indicator = withSafeRender(IndicatorImpl, "Indicator");

export default Indicator;
